import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Star, ChevronDown, ChevronUp, Gift, Map, Target, Shield, FileSignature, Eye, Brain, BarChart3, Clock, Flame, CheckCircle } from 'lucide-react';
import { trackInitiateCheckout } from '../utils/trackAddToCart';
import { OrderBump } from './OrderBump';
const TestimonialCard = ({
  name,
  role,
  quote,
  delay,
  image
}: {
  name: string;
  role: string;
  quote: string;
  delay: number;
  image: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  delay,
  duration: 0.5
}} className="bg-[#0a0a0a] border border-[#222] p-6 rounded-xl flex flex-col h-full" data-id="element-348">
    <div className="flex gap-1 mb-4" data-id="element-349">
      {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[#d4af37] text-[#d4af37]" data-id="element-350" />)}
    </div>
    <p className="text-[#8a8578] italic text-sm mb-6 flex-grow leading-relaxed" data-id="element-351">
      "{quote}"
    </p>
    <div className="mt-auto flex items-center gap-3" data-id="element-352">
      <img src={image} alt={name} className="w-10 h-10 rounded-full object-cover border-2 border-[#d4af37]/30 flex-shrink-0" loading="lazy" decoding="async" data-id="element-353" />
      <div data-id="element-354">
        <p className="text-[#f5f0e8] font-bold text-sm" data-id="element-355">{name}</p>
        <p className="text-[#555] text-xs uppercase tracking-wide" data-id="element-356">{role}</p>
      </div>
    </div>
  </motion.div>;
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-[#222]" data-id="element-357">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex justify-between items-center text-left group" data-id="element-358">
        <span className={`text-lg font-medium transition-colors ${isOpen ? 'text-[#d4af37]' : 'text-[#f5f0e8] group-hover:text-[#d4af37]'}`} data-id="element-359">
          {question}
        </span>
        {isOpen ? <ChevronUp className="text-[#d4af37]" data-id="element-360" /> : <ChevronDown className="text-[#8a8578]" data-id="element-361" />}
      </button>
      <AnimatePresence data-id="element-362">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-363">
            <p className="text-[#8a8578] pb-6 leading-relaxed" data-id="element-364">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
interface OfferSectionProps {
  isOrderBumpChecked: boolean;
  onOrderBumpToggle: () => void;
}
export function OfferSection({
  isOrderBumpChecked,
  onOrderBumpToggle
}: OfferSectionProps) {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  return <section id="comprar" className="w-full bg-[#050505] py-24 px-4 pb-40" data-id="element-365">
      <div className="max-w-5xl mx-auto" data-id="element-366">
        {/* INCLUSIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24" data-id="element-367">
          <div data-id="element-368">
            <h3 className="font-serif text-3xl text-[#f5f0e8] mb-8" data-id="element-369">
              Qué incluye el PDF
            </h3>
            <ul className="space-y-4" data-id="element-370">
              {['Las 4 leyes mentales aterrizadas al trading', 'Explicación mente consciente vs subconsciente', 'Semáforo verde/amarillo/rojo', 'Checklist de 1 minuto para entrar', 'PP-7 completo con respuestas de intervención', 'Guiones de 60 segundos para cortar venganza', 'Cierre de sesión para evitar espiral', 'Diario mínimo (5 preguntas)', 'Plan de 14 días de instalación'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#8a8578]" data-id="element-371">
                  <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-372" />
                  <span data-id="element-373">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="space-y-8" data-id="element-374">
            <div className="bg-[#0a0a0a] border border-[#222] p-6 rounded-lg" data-id="element-375">
              <h4 className="text-[#f5f0e8] font-medium mb-4 flex items-center gap-2" data-id="element-376">
                <Check className="w-4 h-4 text-green-500" data-id="element-377" /> Esto es para ti si:
              </h4>
              <ul className="space-y-2 text-sm text-[#8a8578]" data-id="element-378">
                <li data-id="element-379">• Rompes reglas después de ganar o perder</li>
                <li data-id="element-380">• Te cuesta cerrar sesión</li>
                <li data-id="element-381">• Te da vergüenza admitir que te saboteas</li>
                <li data-id="element-382">• Quieres convertirte en el trader que admiras</li>
              </ul>
            </div>

            <div className="bg-[#0a0a0a] border border-[#222] p-6 rounded-lg opacity-70" data-id="element-383">
              <h4 className="text-[#f5f0e8] font-medium mb-4 flex items-center gap-2" data-id="element-384">
                <X className="w-4 h-4 text-red-500" data-id="element-385" /> No es para ti si:
              </h4>
              <ul className="space-y-2 text-sm text-[#8a8578]" data-id="element-386">
                <li data-id="element-387">• Buscas señales o entradas mágicas</li>
                <li data-id="element-388">• Quieres evitar pérdidas a toda costa</li>
                <li data-id="element-389">• Quieres espiritualidad sin estructura</li>
                <li data-id="element-390">• No estás dispuesto a seguir un protocolo</li>
              </ul>
            </div>
          </div>
        </div>

        {/* TESTIMONIALS */}
        <div className="mb-24" data-id="element-391">
          <h3 className="text-center font-serif text-3xl md:text-4xl text-[#f5f0e8] mb-4" data-id="element-392">
            No es magia. Es <span className="text-[#d4af37]" data-id="element-393">identidad.</span>
          </h3>
          <p className="text-center text-[#8a8578] mb-6 max-w-2xl mx-auto" data-id="element-394">
            Lo que sucede cuando dejas de pelear con el gráfico y empiezas a
            gobernar tus impulsos.
          </p>

          {/* Social proof badge */}
          <div className="flex justify-center mb-10" data-id="element-395">
            <div className="inline-flex items-center gap-2 bg-[#0a0a0a] border border-[#222] rounded-full px-5 py-2.5" data-id="element-396">
              <span className="relative flex h-2.5 w-2.5" data-id="element-397">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00C853] opacity-75" data-id="element-398"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00C853]" data-id="element-399"></span>
              </span>
              <span className="text-sm text-[#8a8578]" data-id="element-400">
                <span className="text-[#f5f0e8] font-bold" data-id="element-401">65+ traders</span> lo
                aplicaron en los últimos 2 meses
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-402">
            <TestimonialCard name="Carlos M." role="Trader de Forex · 3 años" image="https://cdn.magicpatterns.com/uploads/aba6L1M3RyiNovY4vEeKnw/www.thispersondoesnotexist.jpg" quote="Llevaba 3 años atrapado en el mismo ciclo: ganaba durante semanas y en una sola tarde de furia quemaba todo. El PP-7 me dio algo que ningún mentor técnico me enseñó: un freno de emergencia real. Por primera vez en años, cerré la plataforma en rojo sin sentir la necesidad de vengarme." delay={0} data-id="element-403" />
            <TestimonialCard name="Andrea R." role="Trader de Crypto · 1 año" image="https://cdn.magicpatterns.com/uploads/hfKzDsskmFD6dh6y2BJ1i6/www.thispersondoesnotexist.jpg" quote="Me daba terror entrar al mercado. Dudaba de cada setup aunque cumpliera mis reglas. El Diagnóstico de Termostato me hizo ver que mi problema no era la estrategia, era que operaba desde la escasez. Hoy opero con una frialdad que ni yo misma reconozco." delay={0.1} data-id="element-404" />
            <TestimonialCard name="Miguel Á." role="Trader de Índices · 5 años" image="https://cdn.magicpatterns.com/uploads/qUu742s13PkLXEw3KhaiiB/www.thispersondoesnotexist.jpg" quote="Pensé que necesitaba otro curso de Smart Money. Estaba equivocado. Necesitaba dejar de ser mi propio enemigo. El Contrato del Ejecutor cambió mi postura frente a la pantalla. Ya no opero para sentir adrenalina, opero para ejecutar. Brutalmente honesto y directo a la vena." delay={0.2} data-id="element-405" />
          </div>
        </div>

        {/* PERSUASION GUARANTEE */}
        <div className="mb-24 max-w-3xl mx-auto" data-id="element-406">
          <div className="relative p-1 rounded-2xl bg-gradient-to-b from-[#d4af37]/50 to-transparent" data-id="element-407">
            <div className="bg-[#050505] rounded-xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-408">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#d4af37]/10 blur-3xl rounded-full pointer-events-none" data-id="element-409"></div>

              <Shield className="w-12 h-12 text-[#d4af37] mx-auto mb-6" data-id="element-410" />

              <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8] mb-6" data-id="element-411">
                La Garantía de la Verdad Incómoda
              </h3>

              <div className="space-y-4 text-[#8a8578] text-lg leading-relaxed" data-id="element-412">
                <p data-id="element-413">
                  No te voy a prometer que te harás millonario en 30 días. Eso
                  es basura de marketing.
                </p>
                <p data-id="element-414">
                  Mi garantía es mucho más profunda:{' '}
                  <strong className="text-[#f5f0e8]" data-id="element-415">
                    Te garantizo que este manual te va a confrontar.
                  </strong>
                </p>
                <p data-id="element-416">
                  Te va a mostrar exactamente por qué te saboteas, por qué
                  devuelves tus ganancias y por qué sigues tropezando con la
                  misma piedra a pesar de "saber" lo que tienes que hacer.
                </p>
                <p className="text-[#d4af37] font-medium italic pt-4" data-id="element-417">
                  Si después de aplicar el protocolo de 14 días sigues operando
                  desde la necesidad, la venganza o el miedo... entonces el
                  problema no es emocional. Pero si te has sentido identificado
                  con cada palabra de esta página, este sistema te devolverá el
                  control.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PRICING */}
        <div id="checkout" className="max-w-2xl mx-auto text-center mb-24 bg-[#0a0a0a] border border-[#222] rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-418">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a]" data-id="element-419"></div>

          <div className="inline-block p-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] mb-8" data-id="element-420">
            <div className="bg-[#050505] rounded-full px-6 py-2" data-id="element-421">
              <span className="text-[#d4af37] text-sm font-bold tracking-widest uppercase" data-id="element-422">
                Oferta de lanzamiento
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mb-6" data-id="element-423">
            <div className="flex items-center gap-3 mb-2" data-id="element-424">
              <span className="text-[#8a8578] text-xl line-through decoration-red-500/50" data-id="element-425">
                {isOrderBumpChecked ? '$305.000 COP' : '$180.000 COP'}
              </span>
              <span className="bg-red-900/30 text-red-400 text-xs font-bold px-2 py-1 rounded" data-id="element-426">
                {isOrderBumpChecked ? '-69%' : '-22%'}
              </span>
            </div>
            <h2 className="text-5xl md:text-7xl font-serif text-[#f5f0e8] font-bold transition-all duration-300" data-id="element-427">
              ${isOrderBumpChecked ? '78.000' : '38.000'}{' '}
              <span className="text-xl md:text-3xl text-[#8a8578] font-normal" data-id="element-428">
                COP
              </span>
            </h2>
          </div>

          <p className="text-[#8a8578] text-lg mb-10 max-w-lg mx-auto" data-id="element-429">
            Es deliberadamente accesible. Porque el punto no es que pienses "qué
            caro". El punto es que pienses:{' '}
            <span className="text-[#f5f0e8]" data-id="element-430">
              "Es más caro seguir pagando el impuesto de mi impulsividad."
            </span>
          </p>

          {/* BOX 1: WHAT YOU'RE BUYING */}
          <div className="text-left bg-[#080808] border border-[#1a1a1a] rounded-xl p-6 mb-4 max-w-lg mx-auto" data-id="element-431">
            <p className="text-sm font-bold text-[#f5f0e8] mb-4" data-id="element-432">
              Estás comprando:
            </p>
            <ul className="space-y-3" data-id="element-433">
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-434">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-435" />
                <span data-id="element-436">Las 4 leyes mentales aterrizadas al trading</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-437">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-438" />
                <span data-id="element-439">PP-7 completo con respuestas de intervención</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-440">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-441" />
                <span data-id="element-442">RI-3: Reprogramación Identitaria en 14 días</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-443">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-444" />
                <span data-id="element-445">Semáforo + Checklist + Diario mínimo</span>
              </li>
            </ul>
          </div>

          {/* BOX 2: BONUSES */}
          <div className="text-left bg-[#080808] border border-[#1a1a1a] rounded-xl p-6 mb-10 max-w-lg mx-auto" data-id="element-446">
            <div className="flex items-center gap-2 mb-5" data-id="element-447">
              <Gift className="w-5 h-5 text-[#d4af37]" data-id="element-448" />
              <p className="text-sm font-bold text-[#d4af37]" data-id="element-449">
                + 3 Bonos GRATIS{' '}
                <span className="text-[#8a8578] font-normal" data-id="element-450">
                  (Valor{' '}
                  <span className="line-through decoration-red-500/50" data-id="element-451">
                    $186.000 COP
                  </span>
                  )
                </span>
              </p>
            </div>

            {/* Bonus compact list with images */}
            <div className="space-y-4" data-id="element-452">
              <div className="flex items-center gap-3" data-id="element-453">
                <img src="https://cdn.magicpatterns.com/uploads/qaQvkXWJZ8RDR5y9YY3mdE/Bono_1_Imagen.png" alt="Bono 1" className="w-12 h-12 rounded-lg object-cover border border-[#222] flex-shrink-0" loading="lazy" decoding="async" data-id="element-454" />
                <div data-id="element-455">
                  <p className="text-sm font-medium text-[#f5f0e8]" data-id="element-456">
                    Anti-Autosabotaje
                  </p>
                  <p className="text-xs text-[#8a8578]" data-id="element-457">
                    Mapa de Gatillos + Plan de Intervención
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-458">
                <img src="https://cdn.magicpatterns.com/uploads/ooH1SwCackC2np9RuWDeDK/Bono_2.png" alt="Bono 2" className="w-12 h-12 rounded-lg object-cover border border-[#222] flex-shrink-0" loading="lazy" decoding="async" data-id="element-459" />
                <div data-id="element-460">
                  <p className="text-sm font-medium text-[#f5f0e8]" data-id="element-461">
                    Contrato del Ejecutor
                  </p>
                  <p className="text-xs text-[#8a8578]" data-id="element-462">
                    Identidad Trader Rentable (RI-3 en 14 días)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-463">
                <img src="https://cdn.magicpatterns.com/uploads/cpYUmPGveHC3u7DvfiMsDs/Bono_3.png" alt="Bono 3" className="w-12 h-12 rounded-lg object-cover border border-[#222] flex-shrink-0" loading="lazy" decoding="async" data-id="element-464" />
                <div data-id="element-465">
                  <p className="text-sm font-medium text-[#f5f0e8]" data-id="element-466">
                    Rutina PDF (3 min)
                  </p>
                  <p className="text-xs text-[#8a8578]" data-id="element-467">
                    Inicio + En Caliente + Cierre de Sesión
                  </p>
                </div>
              </div>
            </div>

            {/* EXPANDABLE DETAIL */}
            <AnimatePresence data-id="element-468">
              {showBonusDetails && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-469">
                  <div className="pt-5 mt-5 border-t border-[#1a1a1a] space-y-8" data-id="element-470">
                    {/* BONO #1 DETAIL */}
                    <div data-id="element-471">
                      <div className="flex items-center gap-2 mb-3" data-id="element-472">
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-[10px] font-bold tracking-wider" data-id="element-473">
                          BONO #1
                        </span>
                        <span className="text-xs text-[#8a8578]" data-id="element-474">
                          Valor:{' '}
                          <span className="line-through" data-id="element-475">$67.000 COP</span> |{' '}
                          <span className="text-[#d4af37]" data-id="element-476">GRATIS</span>
                        </span>
                      </div>
                      <h4 className="font-serif text-lg text-[#f5f0e8] mb-2" data-id="element-477">
                        Anti-Autosabotaje: Mapa de Gatillos + Plan de
                        Intervención
                      </h4>
                      <p className="text-[#8a8578] text-xs mb-3" data-id="element-478">
                        Este es el bono para el momento donde más te traicionas:
                        cuando tu mente empieza a negociar con la misma frase de
                        siempre…
                      </p>
                      <div className="pl-3 border-l-2 border-[#d4af37]/30 mb-3 italic text-[#d4af37]/80 text-xs space-y-0.5" data-id="element-479">
                        <p data-id="element-480">"Solo un trade más."</p>
                        <p data-id="element-481">"Solo para recuperar."</p>
                        <p data-id="element-482">"Solo porque está clarísimo."</p>
                      </div>
                      <p className="text-[f5f0e8] text-xs font-medium mb-3" data-id="element-483">
                        Aquí no hay teoría. Hay intervención.
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-[#555] mb-2" data-id="element-484">
                        Qué te da:
                      </p>
                      <ul className="space-y-2" data-id="element-485">
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-486">
                          <Map className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-487" />
                          <span data-id="element-488">
                            Un mapa visual de los 7 gatillos que te cuestan
                            dinero y paz
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-489">
                          <Target className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-490" />
                          <span data-id="element-491">
                            Un plan para identificar tu gatillo dominante del
                            día en 90 segundos
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-492">
                          <Shield className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-493" />
                          <span data-id="element-494">
                            La respuesta mecánica para cortar el impulso antes
                            del pico
                          </span>
                        </li>
                      </ul>
                      <p className="text-xs italic text-[#d4af37] mt-3" data-id="element-495">
                        "Porque el autosabotaje no se vence con ganas. Se vence
                        con un protocolo que se activa solo."
                      </p>
                    </div>

                    {/* BONO #2 DETAIL */}
                    <div data-id="element-496">
                      <div className="flex items-center gap-2 mb-3" data-id="element-497">
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-[10px] font-bold tracking-wider" data-id="element-498">
                          BONO #2
                        </span>
                        <span className="text-xs text-[#8a8578]" data-id="element-499">
                          Valor:{' '}
                          <span className="line-through" data-id="element-500">$67.000 COP</span> |{' '}
                          <span className="text-[#d4af37]" data-id="element-501">GRATIS</span>
                        </span>
                      </div>
                      <h4 className="font-serif text-lg text-[#f5f0e8] mb-2" data-id="element-502">
                        Identidad Trader Rentable: El Contrato del Ejecutor
                      </h4>
                      <p className="text-[#8a8578] text-xs mb-3" data-id="element-503">
                        Este bono es confrontativo, porque es lo que casi nadie
                        quiere aceptar:
                      </p>
                      <div className="pl-3 border-l-2 border-[#d4af37]/30 mb-3 italic text-[#d4af37]/80 text-xs" data-id="element-504">
                        <p data-id="element-505">
                          "Si no está escrito, se negocia. Si se negocia, se
                          rompe. Y si se rompe, tu identidad se parte en dos."
                        </p>
                      </div>
                      <p className="text-[10px] uppercase tracking-widest text-[#555] mb-2" data-id="element-506">
                        Qué te da:
                      </p>
                      <ul className="space-y-2" data-id="element-507">
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-508">
                          <FileSignature className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-509" />
                          <span data-id="element-510">
                            Un Contrato del Ejecutor (firmable) con reglas no
                            negociables
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-511">
                          <Eye className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-512" />
                          <span data-id="element-513">
                            Enfoque brutal en ambiente (setup, redes,
                            notificaciones, fatiga)
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-514">
                          <Brain className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-515" />
                          <span data-id="element-516">
                            El ciclo: Ambiente → Pensamiento → Sentimiento →
                            Acción → Resultado
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-517">
                          <BarChart3 className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-518" />
                          <span data-id="element-519">
                            Sistema de evidencia diaria (60 seg) que instala
                            identidad
                          </span>
                        </li>
                      </ul>
                      <p className="text-xs italic text-[#d4af37] mt-3" data-id="element-520">
                        "Porque el trader rentable no es el que sabe más. Es el
                        que ejecuta igual cuando gana, cuando pierde y cuando el
                        ego aprieta."
                      </p>
                    </div>

                    {/* BONO #3 DETAIL */}
                    <div data-id="element-521">
                      <div className="flex items-center gap-2 mb-3" data-id="element-522">
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-[10px] font-bold tracking-wider" data-id="element-523">
                          BONO #3
                        </span>
                        <span className="text-xs text-[#8a8578]" data-id="element-524">
                          Valor:{' '}
                          <span className="line-through" data-id="element-525">$51.000 COP</span> |{' '}
                          <span className="text-[#d4af37]" data-id="element-526">GRATIS</span>
                        </span>
                      </div>
                      <h4 className="font-serif text-lg text-[#f5f0e8] mb-2" data-id="element-527">
                        Rutina PDF: Inicio + En Caliente + Cierre de Sesión
                      </h4>
                      <p className="text-[#8a8578] text-xs mb-3" data-id="element-528">
                        Si tu sesión empieza sin ritual, empieza con
                        improvisación. Y si termina sin cierre, termina con
                        espiral.
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-[#555] mb-2" data-id="element-529">
                        Qué te da:
                      </p>
                      <ul className="space-y-2" data-id="element-530">
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-531">
                          <Clock className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-532" />
                          <span data-id="element-533">
                            Inicio en 90 seg: semáforo + regla del día +
                            checklist
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-534">
                          <Flame className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-535" />
                          <span data-id="element-536">
                            Intervención en caliente en 45 seg: pausa +
                            respiración + regla mecánica
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-537">
                          <CheckCircle className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-538" />
                          <span data-id="element-539">
                            Cierre en 90 seg: evidencia mínima + ajuste para
                            mañana (sin castigo)
                          </span>
                        </li>
                      </ul>
                      <p className="text-xs italic text-[#d4af37] mt-3" data-id="element-540">
                        "Porque el autosabotaje casi siempre ocurre cuando
                        entras sin estado o te quedas emocionalmente abierto al
                        cerrar."
                      </p>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* LEER MÁS TOGGLE */}
            <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="flex items-center gap-1.5 mx-auto mt-5 pt-4 border-t border-[#1a1a1a] text-sm text-[#d4af37] hover:text-[#e8c84a] transition-colors" data-id="element-541">
              <span data-id="element-542">{showBonusDetails ? 'Leer menos' : 'Leer más'}</span>
              {showBonusDetails ? <ChevronUp className="w-4 h-4" data-id="element-543" /> : <ChevronDown className="w-4 h-4" data-id="element-544" />}
            </button>
          </div>

          <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="inline-block w-full md:w-auto px-12 py-4 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a] text-black font-bold text-lg tracking-wide rounded hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-545">
            CONSIGUE TRADING EN PAZ
          </a>

          <p className="mt-4 text-xs text-[#555]" data-id="element-546">
            Entrega digital inmediata (PDF)
          </p>

          <div className="max-w-xl mx-auto" data-id="element-547">
            <OrderBump isChecked={isOrderBumpChecked} onToggle={onOrderBumpToggle} data-id="element-548" />

            {/* PRICE BREAKDOWN */}
            <div className="mt-8 bg-[#0a0a0a] border border-[#222] rounded-xl p-6 text-left" data-id="element-549">
              <div className="space-y-3" data-id="element-550">
                <div className="flex justify-between items-center text-sm" data-id="element-551">
                  <span className="text-[#8a8578]" data-id="element-552">Trading en Paz (PDF)</span>
                  <div className="flex items-center gap-2" data-id="element-553">
                    <span className="text-[#555] line-through text-xs" data-id="element-554">
                      $180.000
                    </span>
                    <span className="text-[#f5f0e8] font-medium" data-id="element-555">$38.000</span>
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm" data-id="element-556">
                  <span className="text-[#8a8578]" data-id="element-557">3 Bonos de Lujo</span>
                  <div className="flex items-center gap-2" data-id="element-558">
                    <span className="text-[#555] line-through text-xs" data-id="element-559">
                      $186.000
                    </span>
                    <span className="text-[#00C853] font-medium" data-id="element-560">GRATIS</span>
                  </div>
                </div>

                {isOrderBumpChecked && <div className="flex justify-between items-center text-sm" data-id="element-561">
                    <span className="text-[#8a8578]" data-id="element-562">
                      Psicología del Dinero
                    </span>
                    <div className="flex items-center gap-2" data-id="element-563">
                      <span className="text-[#555] line-through text-xs" data-id="element-564">
                        $233.000
                      </span>
                      <span className="text-[#f5f0e8] font-medium" data-id="element-565">
                        $40.000
                      </span>
                    </div>
                  </div>}

                <div className="border-t border-[#222] pt-3 mt-3" data-id="element-566">
                  <div className="flex justify-between items-center text-sm mb-1" data-id="element-567">
                    <span className="text-[#8a8578]" data-id="element-568">Valor Total</span>
                    <span className="text-[#555] line-through text-xs" data-id="element-569">
                      {isOrderBumpChecked ? '$491.000' : '$257.000'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center" data-id="element-570">
                  <span className="text-[#d4af37] font-bold text-lg" data-id="element-571">
                    Hoy pagas solo
                  </span>
                  <span className="text-[#f5f0e8] font-bold text-2xl font-serif" data-id="element-572">
                    ${isOrderBumpChecked ? '78.000' : '38.000'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 14 DAY PLAN */}
        <div className="border-t border-[#222] pt-16 mb-24" data-id="element-573">
          <h3 className="text-center font-serif text-2xl text-[#f5f0e8] mb-12" data-id="element-574">
            La Regla de 14 Días
          </h3>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#8a8578] max-w-4xl mx-auto" data-id="element-575">
            <div className="flex flex-col items-center gap-2" data-id="element-576">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-577">
                1
              </span>
              <span data-id="element-578">Semáforo</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-579"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-580">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-581">
                2
              </span>
              <span data-id="element-582">Checklist</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-583"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-584">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-585">
                3
              </span>
              <span data-id="element-586">PP-7</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-587"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-588">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-589">
                4
              </span>
              <span data-id="element-590">Cierre</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-591"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-592">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-593">
                5
              </span>
              <span data-id="element-594">Diario</span>
            </div>
          </div>
        </div>

        {/* FAQ SECTION */}
        <div className="max-w-3xl mx-auto mb-24" data-id="element-595">
          <h3 className="text-center font-serif text-2xl text-[#f5f0e8] mb-12" data-id="element-596">
            Preguntas Frecuentes
          </h3>
          <div className="space-y-2" data-id="element-597">
            <FAQItem question="¿Es solo un PDF?" answer="Sí, y eso es intencional. No necesitas otro curso de 40 horas que no vas a terminar. Necesitas un sistema simple que puedas imprimir, pegar donde operas, y aplicar HOY. Trading en Paz está diseñado para ser una herramienta de uso diario, no contenido para consumir y olvidar." data-id="element-598" />
            <FAQItem question="¿Funciona para mi mercado (crypto, forex, acciones, índices)?" answer="Sí. El sabotaje emocional no distingue entre mercados. Si operas con dinero real y sientes presión, este sistema aplica. Los gatillos (FOMO, venganza, euforia) son universales." data-id="element-599" />
            <FAQItem question="¿Cuánto tiempo toma ver resultados?" answer="El plan está diseñado para 14 días. No vas a convertirte en otro trader de la noche a la mañana, pero en 14 días de aplicación consistente vas a notar algo poderoso: empiezas a verte diferente. Y cuando te ves diferente, operas diferente." data-id="element-600" />
            <FAQItem question="¿Esto reemplaza mi estrategia de trading?" answer="No. Trading en Paz no te dice cuándo entrar o salir del mercado. Te da el sistema mental para que EJECUTES tu estrategia sin sabotearte. Es el complemento que tu estrategia necesita para funcionar de verdad." data-id="element-601" />
            <FAQItem question="¿Por qué tan barato?" answer="Porque una sola sesión impulsiva te cuesta más que esto. El precio no es el punto. El punto es que dejes de pagar el impuesto invisible de tu impulsividad. Si $38.000 te parece mucho, piensa en cuánto te costó tu último trade de venganza." data-id="element-602" />
            <FAQItem question="¿Qué pasa si no me funciona?" answer="Si aplicas el protocolo 14 días y no notas ningún cambio en tu forma de operar, el problema probablemente no es emocional — es estratégico. Pero si te reconoces en esta página, este PDF fue escrito para ti." data-id="element-603" />
          </div>
        </div>

        {/* CLOSING */}
        <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden flex flex-col md:flex-row border border-[#222]" data-id="element-604">
          <div className="md:w-1/2 relative min-h-[300px]" data-id="element-605">
            <img src="https://cdn.magicpatterns.com/uploads/8BB53BHWVDSfnPogEn3tbw/Imagen_mocapck_actualizada.png" alt="Trader desk" className="absolute inset-0 w-full h-full object-cover opacity-80" loading="lazy" decoding="async" data-id="element-606" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" data-id="element-607"></div>
          </div>
          <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center" data-id="element-608">
            <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-609">
              Tu mayor riesgo es esta frase: <br data-id="element-610" />
              <span className="text-[#d4af37]" data-id="element-611">
                "Yo sé lo que tengo que hacer… pero no lo hago."
              </span>
            </h3>
            <p className="text-[#8a8578] mb-8" data-id="element-612">
              Trading en Paz te da lo que faltaba: un puente entre el tú
              racional y el tú reactivo. Aplicalo 14 días. No para "ganar más".
              Para dejar de ser peligroso para ti.
            </p>
            <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="self-start px-8 py-3 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors duration-300 uppercase tracking-wider text-sm font-bold" data-id="element-613">
              DESCARGAR AHORA
            </a>
          </div>
        </div>
      </div>
    </section>;
}